import { useState } from 'react';
import { sendLoginEmailCode } from '../../../shared/features/session/session.api';
import { apiErrorMessage } from '../../../shared/policies/api-error-message';

/** `sent` once an email has gone out for this login, so the form can say where to look and offer "Send another code". */
export type EmailCodeDelivery = 'none' | 'sending' | 'sent';

/**
 * Sending the login's email code, and on a test deployment that shows codes instead of emailing them, the code
 * itself. Errors go to the login step's own error, so the form shows one message whichever part failed.
 */
export function useLoginEmailCode(setError: (error: string | null) => void) {
  const [delivery, setDelivery] = useState<EmailCodeDelivery>('none');
  const [displayedCode, setDisplayedCode] = useState<string | null>(null);

  async function send() {
    setError(null);
    setDelivery('sending');
    try {
      const sent = await sendLoginEmailCode();
      setDisplayedCode(sent.displayedCode);
      setDelivery('sent');
    } catch (err) {
      setDelivery((current) => (current === 'sending' ? 'none' : current));
      setError(apiErrorMessage(err) ?? "Couldn't send the code. Try again.");
    }
  }

  function reset() {
    setDelivery('none');
    setDisplayedCode(null);
  }

  return { delivery, displayedCode, send, reset };
}
